import { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import intlTelInput from "intl-tel-input"
import { Api } from "../../api/Assinafy"
import { Alert, Button, Form, Input } from "../../components"
import { Page, Section } from "../../layout"
import { getIntlTelValidationMessage } from "../../utils/Formats"

import "./styles.scss"

const SignerConfirmData = () => {
  const { signerAccessCode, document_id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location

  const [signer, setSigner] = useState({})
  const [formData, setFormData] = useState({
    whatsapp_phone_number: "",
    full_name: "",
    email: "",
  })
  const [preFilledFields, setPreFilledFields] = useState({
    full_name: false,
    email: false,
    whatsapp_phone_number: false,
  })
  /** Evita montar PhoneInternational antes da API: trocar depois quebra o DOM do intl-tel-input. */
  const [signerPhoneFieldReady, setSignerPhoneFieldReady] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState({})
  const [fieldErrors, setFieldErrors] = useState({
    full_name: "",
    email: "",
    whatsapp_phone_number: "",
  })

  useEffect(() => {
    if (!state?.verified) {
      navigate(`/verify/${signerAccessCode}`)
      return
    }

    Api("signer/self")(signerAccessCode)
      .then(({ status, data: { data } }) => {
        if (status === 200) {
          setSigner(data)
          setFormData({
            full_name: data.full_name || "",
            email: data.email || "",
            whatsapp_phone_number: data.whatsapp_phone_number || "",
          })
          setPreFilledFields({
            full_name: !!(data.full_name && data.full_name.trim()),
            email: !!(data.email && data.email.trim()),
            whatsapp_phone_number: !!(data.whatsapp_phone_number && data.whatsapp_phone_number.trim()),
          })
        }
      })
      .catch(({ response }) => {
        if (response) {
          setFormError({
            type: "warning",
            message: response.data.message || "Erro ao carregar dados do signatário",
          })
        }
      })
      .finally(() => {
        setSignerPhoneFieldReady(true)
      })
  }, [signerAccessCode, state, navigate, document_id])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setFormLoading(true)
    setFormError({})
    setFieldErrors({
      full_name: "",
      email: "",
      whatsapp_phone_number: "",
    })

    let hasError = false
    const errors = {}

    if (!formData.full_name || formData.full_name.trim() === "") {
      errors.full_name = "O nome é obrigatório."
      hasError = true
    }

    const trimmedEmail = String(formData.email || "").trim()
    if (preFilledFields.email && !trimmedEmail) {
      errors.email = "O e-mail é obrigatório."
      hasError = true
    } else if (trimmedEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(trimmedEmail)) {
        errors.email = "Por favor, insira um e-mail válido."
        hasError = true
      }
    }

    const phoneEl = document.getElementById("whatsapp_phone_number")
    const iti = phoneEl?.iti
    const rawPhoneDigits = String(formData.whatsapp_phone_number || "").replace(/\D/g, "")
    const selectedDialCode = String(iti?.getSelectedCountryData?.()?.dialCode || "")
    // Quando só existe o DDI automático do plugin, tratar como campo vazio.
    const hasPhoneValue =
      rawPhoneDigits.length > 0 &&
      (!selectedDialCode || rawPhoneDigits.length > selectedDialCode.length)

    if (hasPhoneValue && !preFilledFields.whatsapp_phone_number) {
      const isConstraintAvailable = phoneEl && typeof phoneEl.checkValidity === "function"

      if (isConstraintAvailable && !phoneEl.checkValidity()) {
        errors.whatsapp_phone_number =
          phoneEl.validationMessage || "Número de telefone inválido."
        hasError = true
      } else {
        if (iti && intlTelInput?.utils) {
          const valid = iti.isValidNumber()
          if (valid === false) {
            errors.whatsapp_phone_number = getIntlTelValidationMessage(
              intlTelInput,
              iti.getValidationError()
            )
            hasError = true
          }
        }
      }
    }

    if (hasError) {
      setFormLoading(false)
      setFieldErrors(errors)
      return
    }

    const actualDocumentId = document_id || state?.documentId

    if (!actualDocumentId) {
      setFormLoading(false)
      setFormError({
        type: "danger",
        message: "ID do documento não foi fornecido. Por favor, volte e tente novamente.",
      })
      return
    }

    const payload = {
      ...(String(formData.email || "").trim() ? { email: String(formData.email).trim() } : {}),
      ...(hasPhoneValue ? { whatsapp_phone_number: formData.whatsapp_phone_number } : {}),
    }

    Api("signer/confirmData")(
      actualDocumentId,
      payload,
      signerAccessCode,
    )
      .then(({ status }) => {
        if (status === 200) {
          navigate(`/sign/${signerAccessCode}`, {
            state: {
              verified: true,
              dataConfirmed: true,
              confirmedData: formData,
              documentId: actualDocumentId,
              ownerName: state?.ownerName || signer?.sender_name || "Proprietário do documento",
              documentName: state?.documentName || signer?.document_name || "documento",
              verification_method: state?.verification_method || "Email",
            },
          })
        }
      })
      .catch(({ response }) => {
        setFormLoading(false)
        if (response) {
          setFormError({
            type: "warning",
            message: response.data.message || "Erro ao confirmar dados",
          })
        }
      })
  }

  const ownerName = state?.ownerName || signer?.sender_name || "Proprietário do documento"

  return (
    <Page className="page-sign">
      <Section>
        <Section.Header
          title={`${ownerName} enviou um documento para ser revisado e assinado por você`}
          opening="Confirme sua identidade abaixo para iniciar o processo de assinatura do documento."
        />
        <Form id="form-confirm-signer-data" className="form-sign" onSubmit={handleSubmit}>
          {formError.message && (
            <Alert variant={formError.type} onClose={() => setFormError({})}>
              {formError.message}
            </Alert>
          )}

          <Form.Group>
            <label htmlFor="full_name">Nome</label>
            <Input
              id="full_name"
              name="full_name"
              type="text"
              placeholder="Nome"
              value={formData.full_name}
              onChange={handleInputChange}
              required
              readOnly={preFilledFields.full_name}
            />
            {fieldErrors.full_name && (
              <div className="help-block">
                {fieldErrors.full_name}
              </div>
            )}
          </Form.Group>

          <Form.Group>
            <label htmlFor="email">E-mail</label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="E-mail"
              value={formData.email}
              onChange={handleInputChange}
              readOnly={preFilledFields.email}
            />
            {fieldErrors.email && (
              <div className="help-block">
                {fieldErrors.email}
              </div>
            )}
          </Form.Group>

          <Form.Group>
            <label htmlFor="whatsapp_phone_number">Telefone</label>
            {!signerPhoneFieldReady ? (
              <Input
                type="text"
                placeholder="Carregando…"
                disabled
                aria-busy="true"
              />
            ) : preFilledFields.whatsapp_phone_number ? (
              <Input
                id="whatsapp_phone_number"
                name="whatsapp_phone_number"
                type="text"
                placeholder="Telefone"
                value={formData.whatsapp_phone_number}
                onChange={handleInputChange}
                readOnly
              />
            ) : (
              <Input.PhoneInternational
                id="whatsapp_phone_number"
                name="whatsapp_phone_number"
                placeholder="Telefone"
                defaultValue={formData.whatsapp_phone_number}
                onChange={handleInputChange}
              />
            )}
            {fieldErrors.whatsapp_phone_number && (
              <div className="help-block">
                {fieldErrors.whatsapp_phone_number}
              </div>
            )}
          </Form.Group>

          <Form.Footer>
            <Button variant="primary" submit size="lg" loading={formLoading}>
              CONFIRMAR DADOS
            </Button>
          </Form.Footer>
        </Form>
      </Section>
    </Page>
  )
}

export default SignerConfirmData
